import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import api from '../../utils/api';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow, Button, TextField, CircularProgress, Snackbar, Alert, Grid, FormControl, InputLabel, Select, MenuItem, IconButton, Tooltip, Chip
} from '@mui/material';
import { Visibility, Download } from '@mui/icons-material';

const WithdrawalRequestsHistory = () => {
  const { adminUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [withdrawals, setWithdrawals] = useState([]);
  const [earnings, setEarnings] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [filters, setFilters] = useState({
    status: 'all',
    doctorName: '',
    startDate: '',
    endDate: '',
  });

  useEffect(() => {
    if (!adminUser || !adminUser.accessToken) {
      navigate('/admin-login');
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
      try {
        const [withdrawalsRes, earningsRes] = await Promise.all([
          api.get('/admin/withdrawals', config),
          api.get('/admin/earnings-payments', config),
        ]);

        setWithdrawals(withdrawalsRes.data);
        const doctorEarningsMap = earningsRes.data.breakdown.reduce((map, e) => {
          const key = e.doctorId;
          if (!map[key]) {
            map[key] = {
              doctorId: e.doctorId,
              doctorName: e.doctorName,
              state: e.state,
              city: e.city,
              totalPaid: 0,
              doctorFee: 0,
              patientCommission: 0,
              doctorCommission: 0,
              finalDoctorEarning: 0,
              totalWithdrawn: 0,
              pendingEarnings: 0,
              appointmentIds: [],
            };
          }
          map[key].totalPaid += e.totalPaid;
          map[key].doctorFee += e.doctorFee;
          map[key].patientCommission += e.patientCommission;
          map[key].doctorCommission += e.doctorCommission;
          map[key].finalDoctorEarning += e.finalDoctorEarning;
          map[key].appointmentIds.push(e.appointmentId);
          return map;
        }, {});

        const aggregatedEarnings = Object.values(doctorEarningsMap).map(e => {
          const doctorWithdrawals = withdrawalsRes.data.filter(w => w.doctorId === e.doctorId && w.status === 'approved');
          const totalWithdrawn = doctorWithdrawals.reduce((sum, w) => sum + w.amount, 0);
          const pendingEarnings = e.finalDoctorEarning - totalWithdrawn;
          return { ...e, totalWithdrawn, pendingEarnings };
        });

        setEarnings(aggregatedEarnings);
      } catch (err) {
        console.error('Fetch error:', err.response?.data || err.message);
        setSnackbar({ open: true, message: 'Failed to fetch withdrawal history', severity: 'error' });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [adminUser, navigate]);

  const filteredWithdrawals = withdrawals.filter(w => {
    const matchesStatus = filters.status === 'all' || w.status === filters.status;
    const matchesDoctorName = !filters.doctorName || w.doctorName.toLowerCase().includes(filters.doctorName.toLowerCase());
    const matchesStartDate = !filters.startDate || new Date(w.requestDate) >= new Date(filters.startDate);
    const matchesEndDate = !filters.endDate || new Date(w.requestDate) <= new Date(filters.endDate);
    return matchesStatus && matchesDoctorName && matchesStartDate && matchesEndDate;
  });

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  if (loading) return <CircularProgress />;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>Withdrawal Requests History</Typography>
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>Filter Withdrawal Requests</Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })}>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField label="Doctor Name" value={filters.doctorName} onChange={e => setFilters({ ...filters, doctorName: e.target.value })} fullWidth />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField type="date" label="Start Date" value={filters.startDate} onChange={e => setFilters({ ...filters, startDate: e.target.value })} fullWidth InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField type="date" label="End Date" value={filters.endDate} onChange={e => setFilters({ ...filters, endDate: e.target.value })} fullWidth InputLabelProps={{ shrink: true }} />
          </Grid>
        </Grid>

        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Request Date & Time</TableCell>
              <TableCell>Doctor Name</TableCell>
              <TableCell>State</TableCell>
              <TableCell>City</TableCell>
              <TableCell>Amount Requested</TableCell>
              <TableCell>Available Balance</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Payment Method</TableCell>
              <TableCell>Date & Time of Payment/Rejection</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredWithdrawals.length > 0 ? (
              filteredWithdrawals.map(w => {
                const doctorEarnings = earnings.find(e => e.doctorId === w.doctorId)?.finalDoctorEarning || 0;
                const withdrawnAmount = withdrawals.filter(wd => wd.doctorId === w.doctorId && wd.status === 'approved').reduce((sum, wd) => sum + wd.amount, 0);
                const availableBalance = doctorEarnings - withdrawnAmount;

                return (
                  <TableRow key={w._id}>
                    <TableCell>{new Date(w.requestDate).toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })}</TableCell>
                    <TableCell>{w.doctorName}</TableCell>
                    <TableCell>{w.state || 'N/A'}</TableCell>
                    <TableCell>{w.city || 'N/A'}</TableCell>
                    <TableCell>₹{w.amount}</TableCell>
                    <TableCell>₹{availableBalance}</TableCell>
                    <TableCell><Chip label={w.status} color={w.status === 'approved' ? 'success' : w.status === 'rejected' ? 'error' : 'warning'} /></TableCell>
                    <TableCell>{w.method}</TableCell>
                    <TableCell>
                      {w.status === 'approved' && w.dateOfPayment
                        ? new Date(w.dateOfPayment).toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })
                        : w.status === 'rejected' && w.dateOfRejection
                        ? new Date(w.dateOfRejection).toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })
                        : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View Payment Details">
                        <IconButton onClick={() => navigate(`/admin/doctor-payment-details/${w.doctorId._id || w.doctorId}`)}>
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      {w.status === 'approved' && (
                        <Tooltip title="Download Invoice">
                          <IconButton onClick={() => console.log(`Download invoice for ${w._id}`)}>
                            <Download />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={10} align="center">No withdrawal requests found</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default WithdrawalRequestsHistory;